/* global Office window */
import * as React from "react";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import { reset } from "../../state/selectionSlice";
import { getTemplateDefs } from "../../helpers/grid";
import ResizeHandler from "../dragAndDrop/ResizeHandler";
import { getPinsForMoving } from "../../helpers/grid";
import HorizontalFloatingContextMenu from "../contextmenus/HorizontalFloatingContextMenu";
import { cancelDrag } from "../../helpers/dragAndDrop";

const Selection = () => {
  const borderStyle = "2px solid #0065FD";
  const hoverStyle = "2px solid gray";

  const dispatch = useDispatch();
  try {
    Office?.context?.document?.addHandlerAsync(Office.EventType.DocumentSelectionChanged, onSelectionChanged);
  } catch (error) {
    // no reaction
  }

  function onSelectionChanged() {
    dispatch(reset());
    cancelDrag();
  }

  const selection = useSelector((state) => state.selection);
  const cols = useSelector((state) => state.gridd.present.structure.columns);
  const rows = useSelector((state) => state.gridd.present.structure.rows);
  const mergedList = useSelector((state) => state.gridd.present.structure.merged);

  function getDimensions(merged) {
    if (merged?.dir === "right") {
      const pins = getPinsForMoving(cols, window.innerWidth);
      const width = pins[merged.cell.y + merged.length + 1] - pins[merged.cell.y];
      const percentage = (width / (pins[merged.cell.y + 1] - pins[merged.cell.y])) * 100;
      return { width: percentage + "%", height: "calc(100%)" };
    } else if (merged?.dir === "down") {
      const pins = getPinsForMoving(rows, window.innerHeight);
      const height = pins[merged.cell.x + merged.length + 1] - pins[merged.cell.x];
      const percentage = (height / (pins[merged.cell.x + 1] - pins[merged.cell.x])) * 100;
      return { width: "calc(100%)", height: percentage + "%" };
    }

    return {
      width: merged && merged.dir === "right" ? merged.length + 1 + "00%" : "calc(100%)",
      height: merged && merged.dir === "down" ? merged.length + 1 + "00%" : "calc(100%)",
    };
  }

  // Index selected cells, columns, rows and merge data into Sets/Maps keyed by
  // "x,y" once per render, so isCellSelected() can do O(1) lookups per cell
  // instead of the previous O(selectedCells x mergedCells) scans.
  const { selectedCellSet, selectedColumnSet, selectedRowSet, mergeOriginMap, mergeCoveredSet, selectedMergeSpanSet } =
    React.useMemo(() => {
      const cellSet = new Set();
      selection.cells.forEach((c) => cellSet.add(`${c.x},${c.y}`));

      const originMap = new Map();
      const coveredSet = new Set();
      const spanSet = new Set();
      mergedList.forEach((m) => {
        originMap.set(`${m.cell.x},${m.cell.y}`, m);
        const originSelected = cellSet.has(`${m.cell.x},${m.cell.y}`);
        if (m.dir === "right") {
          for (let k = m.cell.y + 1; k <= m.cell.y + m.length; k++) {
            coveredSet.add(`${m.cell.x},${k}`);
          }
          if (originSelected) {
            for (let k = m.cell.y; k <= m.cell.y + m.length; k++) {
              spanSet.add(`${m.cell.x},${k}`);
            }
          }
        } else if (m.dir === "down") {
          for (let k = m.cell.x + 1; k <= m.cell.x + m.length; k++) {
            coveredSet.add(`${k},${m.cell.y}`);
          }
          if (originSelected) {
            for (let k = m.cell.x; k <= m.cell.x + m.length; k++) {
              spanSet.add(`${k},${m.cell.y}`);
            }
          }
        }
      });

      return {
        selectedCellSet: cellSet,
        selectedColumnSet: new Set(selection.columns),
        selectedRowSet: new Set(selection.rows),
        mergeOriginMap: originMap,
        mergeCoveredSet: coveredSet,
        selectedMergeSpanSet: spanSet,
      };
    }, [selection.cells, selection.columns, selection.rows, mergedList]);

  function isCellSelected(i, j) {
    const key = `${i},${j}`;
    const asFirst = selection.cells.length > 0 ? selection.cells[0].x === i && selection.cells[0].y === j : false;
    const asCell = selection.cells.length > 1 ? selectedCellSet.has(key) : false;

    const mergeOriginCell = mergeOriginMap.get(key);

    const isPartOfMerge = mergeCoveredSet.has(key);

    const isSelectedmergeOriginCell = mergeOriginMap.has(key) && selectedCellSet.has(key);

    const isPartOfSelectedMerge = selectedMergeSpanSet.has(key);

    const neighbours = {
      left: false,
      right: false,
      top: false,
      bottom: false,
    };

    if (selectedCellSet.has(`${i},${j - 1}`) || selectedColumnSet.has(j - 1)) {
      neighbours.left = true;
    }
    if (selectedCellSet.has(`${i},${j + 1}`) || selectedColumnSet.has(j + 1)) {
      neighbours.right = true;
    }
    if (selectedCellSet.has(`${i - 1},${j}`) || selectedRowSet.has(i - 1)) {
      neighbours.top = true;
    }
    if (selectedCellSet.has(`${i + 1},${j}`) || selectedRowSet.has(i + 1)) {
      neighbours.bottom = true;
    }

    const isCaseOfMerge = isPartOfSelectedMerge || isSelectedmergeOriginCell;
    if (isCaseOfMerge) {
      mergedList.forEach((m) => {
        if (m.dir === "right" && selectedCellSet.has(`${m.cell.x},${m.cell.y}`)) {
          if (m.cell.x === i && j >= m.cell.y && j <= m.cell.y + m.length) {
            if (j > m.cell.y) {
              neighbours.left = true;
            }
            if (j < m.cell.y + m.length) {
              neighbours.right = true;
            }
          }
        }

        if (m.dir === "down" && selectedCellSet.has(`${m.cell.x},${m.cell.y}`)) {
          if (m.cell.y === j && i >= m.cell.x && i <= m.cell.x + m.length) {
            if (i > m.cell.x) {
              neighbours.top = true;
            }
            if (i < m.cell.x + m.length) {
              neighbours.bottom = true;
            }
          }
        }
      });
    }
    const asRow = selection.rows.includes(i);
    const asColumn = selection.columns.includes(j);
    const isLastInRow = j === cols.length - 1;
    const isLastInCol = i === rows.length - 1;
    const isFirstColumn = selection.columns.length > 0 ? selection.columns[0] === j : false;
    const isFirstRow = selection.rows.length > 0 ? selection.rows[0] === i : false;

    const isSelected = {
      asFirst,
      asCell: asCell || isPartOfSelectedMerge,
      asRow,
      asColumn,
      isAny: asFirst || asCell || asRow || asColumn || isPartOfSelectedMerge,
      isLastInCol,
      isLastInRow,
      neighbours,
      isFirstColumn,
      isFirstRow,
      mergeOriginCell,
      isPartOfMerge,
      isTableSelected: selection.table,
    };
    return isSelected;
  }

  useEffect(() => {
    function handleMouseUp(e) {
      if (e.target.localName !== "img") {
        // this condition above is to exclude buttons and other things to be clicked on the moat to close the selection
        if (e.clientX > window.innerWidth - 24 || e.clientY > window.innerHeight - 24) {
          dispatch(reset());
        }
      }
    }
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mouseup", handleMouseUp);
    };
  });

  return selection.cells.length > 0 ||
    selection.columns.length > 0 ||
    selection.rows.length > 0 ||
    selection.hover.cell !== null ? (
    <div
      id="selection_grid"
      style={{
        display: "grid",
        width: "calc(100% + 1px)",
        height: "calc(100% + 1px)",
        gridTemplateColumns: "28px 1fr 28px",
        gridTemplateRows: "28px 1fr 28px",
        zIndex: 1000,
        position: "absolute",
        pointerEvents: "none",
      }}
    >
      <div
        id="selection_topleft"
        style={{
          height: "28px",
          width: "28px",
          position: "relative",
          zIndex: 99999,
        }}
      >
        <HorizontalFloatingContextMenu />
      </div>

      <div id="selection_top" />
      <div id="selection_topright" />

      <div id="selection_left" />
      <div
        id="selection_cells"
        style={{
          display: "grid",
          width: "100%",
          height: "100%",
          gridTemplateColumns: getTemplateDefs(cols),
          gridTemplateRows: getTemplateDefs(rows),
          position: "relative",
          zIndex: 1,
        }}
      >
        {rows?.map((row, i) =>
          cols?.map((col, j) => {
            const isSelected = isCellSelected(i, j);

            if (isSelected.asFirst || isSelected.asCell) {
              const isMergeOrigin = isSelected.mergeOriginCell !== undefined;
              const isEdgeCell = {
                left: isMergeOrigin || !isSelected.neighbours.left,
                right:
                  (isMergeOrigin &&
                    isSelected.mergeOriginCell.dir === "right" &&
                    j === isSelected.mergeOriginCell.cell.y + isSelected.mergeOriginCell.length) ||
                  !isSelected.neighbours.right,
                top: isMergeOrigin || !isSelected.neighbours.top,
                bottom:
                  (isMergeOrigin &&
                    isSelected.mergeOriginCell.dir === "down" &&
                    i === isSelected.mergeOriginCell.cell.x + isSelected.mergeOriginCell.length) ||
                  !isSelected.neighbours.bottom,
              };

              if (isSelected.isTableSelected && isSelected.isPartOfMerge) {
                const mergeInfo = mergedList.find((m) => {
                  if (m.dir === "right" && m.cell.x === i && j >= m.cell.y && j <= m.cell.y + m.length) {
                    return true;
                  }
                  if (m.dir === "down" && m.cell.y === j && i >= m.cell.x && i <= m.cell.x + m.length) {
                    return true;
                  }
                  return false;
                });

                if (mergeInfo) {
                  isEdgeCell.left = j === mergeInfo.cell.y;
                  isEdgeCell.right = j === mergeInfo.cell.y + (mergeInfo.dir === "right" ? mergeInfo.length : 0);
                  isEdgeCell.top = i === mergeInfo.cell.x;
                  isEdgeCell.bottom = i === mergeInfo.cell.x + (mergeInfo.dir === "down" ? mergeInfo.length : 0);
                }
              }

              return (
                <div
                  id={`selection_cell_${i}_${j}`}
                  key={uuidv4()}
                  style={{
                    ...getDimensions(isSelected.mergeOriginCell),
                    position: "relative",
                    boxSizing: "border-box",
                    borderLeft: isEdgeCell.left ? borderStyle : "0px",
                    borderRight: isEdgeCell.right ? borderStyle : "0px",
                    borderTop: isEdgeCell.top ? borderStyle : "0px",
                    borderBottom: isEdgeCell.bottom ? borderStyle : "0px",
                    margin: 0,
                    zIndex: 1,
                  }}
                ></div>
              );
            }

            if (isSelected.asFirst) {
              return (
                <div
                  id={`selection_cell_${i}_${j}`}
                  key={uuidv4()}
                  style={{
                    ...getDimensions(isSelected.mergeOriginCell),
                    position: "relative",
                    boxSizing: "border-box",
                    border: borderStyle,
                    borderLeft: isSelected.neighbours.left ? "0px" : borderStyle,
                    borderRight: isSelected.neighbours.right ? "0px" : borderStyle,
                    borderTop: isSelected.neighbours.top ? "0px" : borderStyle,
                    borderBottom: isSelected.neighbours.bottom ? "0px" : borderStyle,
                    margin: 0,
                    zIndex: 1,
                  }}
                ></div>
              );
            }

            if (isSelected.asCell) {
              const isLastCellInVerticalMerge =
                isSelected.mergeOriginCell &&
                isSelected.mergeOriginCell.dir === "down" &&
                i === isSelected.mergeOriginCell.cell.x + isSelected.mergeOriginCell.length;
              return (
                <div
                  id={`selection_cell_${i}_${j}`}
                  key={uuidv4()}
                  style={{
                    ...getDimensions(isSelected.mergeOriginCell),
                    position: "relative",
                    boxSizing: "border-box",
                    border: borderStyle,
                    borderLeft: isSelected.neighbours.left ? "0px" : borderStyle,
                    borderRight: isSelected.neighbours.right ? "0px" : borderStyle,
                    borderTop: isSelected.neighbours.top ? "0px" : borderStyle,
                    borderBottom: isLastCellInVerticalMerge
                      ? borderStyle
                      : isSelected.neighbours.bottom
                        ? "0px"
                        : borderStyle,
                    margin: 0,
                    zIndex: 1,
                  }}
                ></div>
              );
            }

            if (isSelected.asColumn) {
              const isSpacer = col[0] === "s";

              return (
                <div
                  id={`selection_cell_${i}_${j}`}
                  key={uuidv4()}
                  style={{
                    position: "relative",
                    boxSizing: "border-box",
                    borderTop: i === 0 ? borderStyle : undefined,
                    borderLeft: isSelected.neighbours.left ? undefined : borderStyle,
                    borderRight: isSelected.neighbours.right ? undefined : borderStyle,
                    borderBottom: isSelected.isLastInCol ? borderStyle : undefined,
                    margin: 0,
                    zIndex: 1,
                    boxShadow: isSpacer
                      ? isSelected.isLastInCol
                        ? "-8px -6px 4px -4px darkgray inset"
                        : "-8px 0px 4px -4px darkgray inset"
                      : undefined,
                  }}
                >
                  {j !== 0 && i === 0 && selection.columns.length === 1 ? (
                    <ResizeHandler i={i} j={j} left={true} cols={cols} rows={rows} />
                  ) : (
                    <></>
                  )}

                  {j < cols.length - 1 && i === 0 && selection.columns.length === 1 ? (
                    <ResizeHandler i={i} j={j} left={false} cols={cols} rows={rows} />
                  ) : (
                    <></>
                  )}
                </div>
              );
            }

            if (isSelected.asRow) {
              const isSpacer = row[0] === "s";

              return (
                <div
                  id={`selection_cell_${i}_${j}`}
                  key={uuidv4()}
                  style={{
                    position: "relative",
                    boxSizing: "border-box",
                    borderLeft: j === 0 ? borderStyle : undefined,
                    borderTop: isSelected.neighbours.top ? undefined : borderStyle,
                    borderBottom: isSelected.neighbours.bottom ? undefined : borderStyle,
                    borderRight: isSelected.isLastInRow ? borderStyle : undefined,
                    margin: 0,
                    zIndex: 1,
                    boxShadow: isSpacer
                      ? isSelected.isLastInRow
                        ? "-6px -8px 4px -4px darkgray inset"
                        : "0px -8px 4px -4px darkgray inset"
                      : undefined,
                  }}
                >
                  {i !== 0 && j === 0 && selection.rows.length === 1 ? (
                    <ResizeHandler i={i} j={j} top={true} cols={cols} rows={rows} />
                  ) : (
                    <></>
                  )}

                  {i < rows.length - 1 && j === 0 && selection.rows.length === 1 ? (
                    <ResizeHandler i={i} j={j} top={false} cols={cols} rows={rows} />
                  ) : (
                    <></>
                  )}
                </div>
              );
            }

            if (selection.hover.cell?.x === i && selection.hover.cell?.y === j) {
              return (
                <div
                  id={`selection_cell_${i}_${j}`}
                  key={uuidv4()}
                  style={{
                    ...getDimensions(isSelected.mergeOriginCell),
                    position: "relative",
                    boxSizing: "border-box",
                    border: hoverStyle,
                    margin: 0,
                    zIndex: 1,
                  }}
                ></div>
              );
            }

            return <div id={`selection_cell_${i}_${j}`} key={uuidv4()}></div>;
          })
        )}
      </div>

      <div id="selection_right" />

      <div id="selection_bottomleft" />

      <div id="selection_bottom" />

      <div id="selection_bottomright" />
    </div>
  ) : (
    <></>
  );
};

export default Selection;
